import type { ReactNode } from 'react';

import { cn } from '@mds/shared/lib/utils';

/**
 * The mark's width at each size. Only the width: the height follows the
 * drawing's own proportions, so a glyph stays square and a wide illustration (a
 * cup on its saucer) is no taller than it is drawn. A square box would pad it
 * top and bottom and stand the title row taller than anything in it.
 */
const ICON_SIZE = {
  md: 'w-10 lg:w-14',
  lg: 'w-13 lg:w-20',
} as const;

export type RuledHeaderProps = {
  /**
   * The line that names the section — "Personality".
   *
   * Written in sentence case and uppercased here, for the same reason as
   * `SectionHeader`: the source stays readable and a screen reader is not
   * handed capitals to spell out.
   */
  title: string;
  /**
   * The chapter number in the pill before the title — "05". Optional: a page
   * numbers its chapters or it does not, and an unnumbered one draws no pill.
   *
   * `aria-hidden`, because it is the page's running order, not part of the
   * section's name; a screen reader announcing "zero five Personality" at
   * every heading is noise.
   */
  number?: string;
  /**
   * A mark before the title: a line glyph (`<LuCoffee />`) or a drawn
   * illustration, such as the café's steaming cup. An element rather than a
   * component, so either fits; the header sizes it, and a line glyph takes the
   * brand's text mint from it while an illustration keeps its own colours.
   * Decorative: the title already names the section.
   */
  icon?: ReactNode;
  /**
   * How large the mark is. `md` sits a little over the title's cap height, a
   * glyph beside the words; `lg` is the menu's illustration, about twice the
   * title's height, which needs the room to show its detail.
   */
  iconSize?: keyof typeof ICON_SIZE;
  /**
   * Where the rule goes. `beside`, the default, runs it out from the end of
   * the title to the edge of the column. `below` draws it under the whole
   * heading in the brand's mint, a thicker line the full width, and lets a
   * long title wrap instead of holding it to one line.
   */
  rule?: 'beside' | 'below';
  /**
   * Sets the whole heading in mint — title and rule together, so the line
   * follows the words it belongs to. `--brand-text`, the mint that can carry
   * type on the light page (the bright one would be 1.4:1 here), on the rule
   * as well so the two are one colour and not two steps of it.
   */
  accent?: boolean;
  /**
   * Which heading level this is. Only the page knows where the section sits,
   * so it is a prop; it changes the element and nothing else.
   */
  as?: 'h2' | 'h3';
  /** The heading's id, for a section that names itself with `aria-labelledby`. */
  id?: string;
  /**
   * A sentence under the title — what the section is for, in one breath. It
   * sits close under the title line, so the two read as one opener, and the
   * header's air to the content below is kept after it.
   */
  description?: string;
  /**
   * A way onwards set at the end of the description's line — "All events →".
   * Only drawn with a description, which is the line it shares.
   */
  action?: ReactNode;
  className?: string;
};

/**
 * A chapter opener: a number pill, a display line, and a rule that runs out to
 * the edge of the column.
 *
 * Not `SectionHeader`, which is a quiet mono label over a band of content. This
 * is the loud one — a display-sized line for a page that reads as a sequence of
 * chapters.
 */
export default function RuledHeader({
  title,
  number,
  icon,
  iconSize = 'md',
  rule = 'beside',
  accent = false,
  as: Heading = 'h2',
  id,
  description,
  action,
  className,
}: RuledHeaderProps) {
  return (
    // The heading owns its own air — a card's worth above, on top of the
    // section's gap, and below to the content a block's worth on a phone
    // (24px; a fixed 48 there was half the screen's section gap again) and
    // 48px from `lg` (well under a whole section's 72, which left the heading
    // floating away from what it names) — so every chapter opens the same way
    // without each call site remembering to. `className` still wins:
    // tailwind-merge keeps the caller's margin.
    <header className={cn('mt-card mb-block lg:mb-12', className)}>
      {/* A mark sits closer to its title than the rule does: it belongs to the
          words, where the rule only runs on from them. */}
      <div className={cn('flex items-center', icon ? 'gap-4 lg:gap-6' : 'gap-4 sm:gap-8')}>
        {/* The pill is charcoal with the bright mint on it — the pairing the
            panel already carries, so the mint is at its 14:1 step and not the
            1.4:1 it would be on the page. */}
        {number && (
          <span
            aria-hidden="true"
            className="shrink-0 rounded-button bg-panel px-3.5 py-1.5 font-mono text-2xs font-semibold text-brand"
          >
            {number}
          </span>
        )}

        {/* `text-brand-text` reaches a line glyph through `currentColor`; an
            illustration paints its own fills and ignores it. */}
        {icon && (
          <span
            aria-hidden="true"
            className={cn(
              'block shrink-0 text-brand-text [&>svg]:block [&>svg]:h-auto [&>svg]:w-full',
              ICON_SIZE[iconSize],
            )}
          >
            {icon}
          </span>
        )}

        {/* `text-chapter` carries the whole spec — Satoshi Black, 28→44px,
            solid leading, -0.02em. From `sm` the title stays on one line and the
            rule beside it is what gives way; on a phone a long title such as
            "Coworking desk packages" is wider than the screen, so there it wraps
            (`min-w-0` lets it shrink in the row) rather than push the page
            sideways. */}
        <Heading
          id={id}
          className={cn(
            'min-w-0 text-chapter uppercase',
            // With the rule below there is nothing beside the title to give
            // way, so it wraps, in even lines.
            rule === 'beside' ? 'sm:whitespace-nowrap' : 'text-balance',
            accent ? 'text-brand-text' : 'text-ink',
          )}
        >
          {title}
        </Heading>

        {/* Not drawn on a phone: there the title wraps and leaves the rule a
            stub beside it. From `sm` the title holds one line, and `min-w-8`
            keeps the rule from collapsing when a long one takes the row. */}
        {rule === 'beside' && (
          <span
            aria-hidden="true"
            className={cn(
              'hidden min-w-8 flex-1 border-t-2 sm:block',
              accent ? 'border-brand-text' : 'border-ink',
            )}
          />
        )}
      </div>

      {/* Under the whole heading, on every width: the title can wrap here, so
          the line never ends up a stub beside it. Always the mint, since it is
          the line that marks the section under an ink title. */}
      {rule === 'below' && (
        <span aria-hidden="true" className="mt-4 block h-0.75 rounded-full bg-brand-text lg:mt-6" />
      )}

      {/* `items-end` so an action sits on the description's last line, and
          `flex-wrap` so on a phone it drops under the sentence instead of
          squeezing it. */}
      {description && (
        <div className="mt-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <p className="max-w-prose text-base text-pretty text-ink-secondary">{description}</p>
          {action}
        </div>
      )}
    </header>
  );
}
